import {Helmet} from '@dr.pogodin/react-helmet';
import {openDeleteDialog} from 'platform/components';
import {match} from 'ts-pattern';

import {isArray} from 'ramda-adjunct';

import {useBulkDeleteDirectSaleVariantMutation} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate, SettingsTemplateHeader} from '@dms/settings/shared';
import {DmsDataGrid, handleApiError, usePermissions} from '@dms/shared';

import {composePath, noop, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

export function WarehouseDirectSaleVariants() {
  const navigate = useNavigate();

  const [warehouseDictionaryCreate] = usePermissions({
    permissionKeys: ['warehouseDictionaryCreate'],
  });

  const [deleteDirectSaleVariants] = useBulkDeleteDirectSaleVariantMutation();

  const actionCallback: ActionCallback = ({actionKey, rowId, refreshData}) => {
    match(actionKey)
      .with('redirectDetail', 'edit', () => {
        navigate(
          composePath(settingsRoutes.warehousesDirectSaleVariantsDetail, {
            params: {id: isArray(rowId) ? rowId[0] : rowId},
          })
        );
      })
      .with('delete', () => {
        const rowIds = isArray(rowId) ? rowId : [rowId];
        openDeleteDialog({
          text: i18n.t('entity.warehouse.labels.deleteSelectedItemsPrompt'),
          onConfirm: () => {
            deleteDirectSaleVariants({directSaleVariantId: rowIds})
              .unwrap()
              .then(() => refreshData())
              .catch(handleApiError);
          },
        });
      })
      .otherwise(noop);
  };

  const header: SettingsTemplateHeader = {
    title: i18n.t('entity.warehouse.labels.directSaleVariants'),
    actions: [
      {
        type: 'button',
        title: i18n.t('entity.warehouse.actions.newDirectSaleVariant'),
        onClick: () => {
          navigate(settingsRoutes.warehousesDirectSaleVariantsCreate);
        },
        isDisabled: !warehouseDictionaryCreate,
        tooltip: !warehouseDictionaryCreate
          ? i18n.t('entity.warehouse.labels.missingWarehouseDictionaryCreatePermission')
          : undefined,
        'data-testid': testIds.settings.warehousesDirectSaleVariants('newDirectSaleVariant'),
      },
    ],
  };

  return (
    <>
      <Helmet title={i18n.t('entity.warehouse.labels.directSaleVariants')} />
      <SettingsTemplate
        data-testid={testIds.settings.warehousesDirectSaleVariants('template')}
        header={header}
      >
        <DmsDataGrid
          gridCode="direct-sale-variant"
          actionCallback={actionCallback}
          data-testid={testIds.settings.warehousesDirectSaleVariants('direct-sale-variant')}
        />
      </SettingsTemplate>
    </>
  );
}
